import api from '@/config/api';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:5004/api';

export interface InvitationDetail {
   id: string;
   inviterId: string;
   inviterName: string;
   recipient_email: string;
   recipient_name?: string;
   status: 'pending' | 'accepted' | 'rejected' | 'invited';
   role: string;
   method: string;
   createdAt: Date | string;
   expiresAt?: Date | string;
   acceptedAt?: Date | string;
   acceptedBy?: string;
   personalMessage?: string;
   customMessage?: string;
   location?: string;
   eventTitle?: string;
   eventId?: string;
   albumId?: string;
   tripId?: string;
}

export interface Creator {
   id: string;
   name: string;
   email?: string;
   profilePicture?: string;
}

export interface AlbumDetails {
   id: string;
   title: string;
   description?: string;
   createdAt: Date | string;
   coverImage?: string;
}

export interface EventDetails {
   id: string;
   title: string;
   description?: string;
   startDate?: Date | string;
   endDate?: Date | string;
   location?: string;
   coverImage?: string;
}

export interface TripDetails {
   id: string;
   title: string;
   description?: string;
   startDate?: Date | string;
   endDate?: Date | string;
   destination?: string;
   coverImage?: string;
}

export interface AcceptedByDetails {
   id: string;
   name: string;
   email?: string;
}

export interface InvitationDetailResponse {
   invitation: InvitationDetail;
   creator: Creator | null;
   album?: AlbumDetails | null;
   event?: EventDetails | null;
   trip?: TripDetails | null;
   acceptedBy?: AcceptedByDetails | null;
   type: 'album' | 'event' | 'trip';
}

export interface InvitationSummaryStats {
   total: number;
   accepted: number;
   pending: number;
   rejected: number;
   invited: number;
   acceptanceRate: string;
}

export interface ExpiredInvitation {
   id: string;
   type: 'album' | 'event' | 'trip';
   inviterId: string;
   inviterName: string;
   recipient_email: string;
   recipient_name?: string;
   status: 'pending' | 'invited';
   role: string;
   method: string;
   createdAt: Date | string;
   expiresAt: Date | string;
   daysOverdue: number;
   personalMessage?: string;
   customMessage?: string;
   location?: string;
   eventTitle?: string;
   eventId?: string;
   albumId?: string;
   tripId?: string;
}

export interface ExpiredInvitationsResponse {
   invitations: ExpiredInvitation[];
   stats: {
      total: number;
      recentlyExpired: number; // <= 7 days overdue
      moderatelyExpired: number; // 8-30 days overdue
      longExpired: number; // > 30 days overdue
      byType: {
         album: number;
         event: number;
         trip: number;
      };
   };
   daysAhead: number;
   query: {
      from: string;
      to: string;
   };
}

export interface InvitationSummary {
   albums: InvitationSummaryStats;
   events: InvitationSummaryStats;
   trips: InvitationSummaryStats;
   total: InvitationSummaryStats;
   expiring: ExpiringInvitationsResponse;
   expired: ExpiredInvitationsResponse;
}

export interface ExpiringInvitation {
   id: string;
   type: 'album' | 'event' | 'trip';
   inviterId: string;
   inviterName: string;
   recipient_email: string;
   recipient_name?: string;
   status: 'pending' | 'invited';
   role: string;
   method: string;
   createdAt: Date | string;
   expiresAt: Date | string;
   daysUntilExpiry: number;
   personalMessage?: string;
   customMessage?: string;
   location?: string;
   eventTitle?: string;
   eventId?: string;
   albumId?: string;
   tripId?: string;
}

export interface ExpiringInvitationsResponse {
   invitations: ExpiringInvitation[];
   stats: {
      total: number;
      critical: number; // <= 1 day
      warning: number; // 2-3 days
      normal: number; // > 3 days
      byType: {
         album: number;
         event: number;
         trip: number;
      };
   };
   daysAhead: number;
   query: {
      from: string;
      to: string;
   };
}

export const invitationsService = {
   // Get album invitation details
   async getAlbumInvitationDetails(invitationId: string): Promise<InvitationDetailResponse> {
      const response = await api.get(`${API_URL}/invitations/album/${invitationId}`);
      return response.data.data;
   },

   // Get event invitation details
   async getEventInvitationDetails(invitationId: string): Promise<InvitationDetailResponse> {
      const response = await api.get(`${API_URL}/invitations/event/${invitationId}`);
      return response.data.data;
   },

   // Get trip invitation details
   async getTripInvitationDetails(invitationId: string): Promise<InvitationDetailResponse> {
      const response = await api.get(`${API_URL}/invitations/trip/${invitationId}`);
      return response.data.data;
   },

   // Get invitation summary for dashboard
   async getInvitationSummary(daysAhead: number = 7): Promise<InvitationSummary> {
      const response = await api.get(`${API_URL}/invitations/summary?days=${daysAhead}`);
      return response.data.data;
   },

   // Generic function to get invitation details based on type
   async getInvitationDetails(
      invitationId: string,
      type: 'album' | 'event' | 'trip'
   ): Promise<InvitationDetailResponse> {
      switch (type) {
         case 'album':
            return this.getAlbumInvitationDetails(invitationId);
         case 'event':
            return this.getEventInvitationDetails(invitationId);
         case 'trip':
            return this.getTripInvitationDetails(invitationId);
         default:
            throw new Error(`Invalid invitation type: ${type}`);
      }
   },
};
